import React from 'react';
import { Link } from 'react-router-dom';
import {
  MEMBERSHIP_PATH,
  formatMembershipDate,
  isGgPlusActive,
  isMembershipCancelScheduled,
  isMembershipCanceledOrEnding,
  membershipCancelNotice,
  membershipPeriodEndLabel,
  membershipPriceLabel,
  membershipStatusLabel,
  readEntitlements,
} from '../../utils/membershipUrl';
import {
  logGgPlusManageClicked,
  logGgPlusPaymentIssueViewed,
  logGgPlusUpgradeClicked,
} from '../../utils/ggPlusAnalytics';
import Gg_plus_reactivate_link from './Gg_plus_reactivate_link.js';
import gg_plus_logo from '../../images/logos/gg_plus/gg_plus_103.png';
import './Gg_plus_shared.css';

const UPSELL_HEADLINE = 'Grow more support';
const UPSELL_BODY =
  'Unlock monthly gifting, gift matching, and new tools that allow family and friends to give more consistently over time.';

/**
 * Account settings hub — GG+ membership summary card.
 * Free users: upsell layout (mockup). Subscribers: status summary + manage link.
 */
function Gg_plus_membership_summary() {
  const ent = readEntitlements();
  const isGgPlus = isGgPlusActive(ent);
  const sub = ent && ent.subscription;
  const level = ent && ent.level;
  const priceLabel = membershipPriceLabel(ent);
  const periodEnd = sub && formatMembershipDate(sub.currentPeriodEnd);
  const cancelScheduled = isMembershipCancelScheduled(ent);
  const cancelNotice = membershipCancelNotice(ent);

  React.useEffect(() => {
    if (level === 'past_due') {
      logGgPlusPaymentIssueViewed({ source: 'account_hub' });
    }
  }, [level]);

  const handleHubLinkClick = () => {
    if (isGgPlus || level === 'past_due') {
      logGgPlusManageClicked({ action: 'view_membership', source: 'account_hub' });
    } else {
      logGgPlusUpgradeClicked({ source: 'account_hub' });
    }
  };

  let linkText = 'Try GG+ free';
  let showUpsellLayout = !isGgPlus && level !== 'past_due';

  if (isGgPlus || level === 'past_due') {
    linkText = cancelScheduled || level === 'canceled' ? 'View GG+' : 'Manage GG+';
    showUpsellLayout = false;
  } else if (!isGgPlus && sub && sub.status === 'canceled') {
    linkText = 'Restart GG+';
    showUpsellLayout = false;
  }

  // Free / never-subscribed — full upsell card (matches account mockup)
  if (showUpsellLayout) {
    return (
      <div className="gg_plus_hub_card gg_plus_hub_card_upsell">
        <img src={gg_plus_logo} alt="GG+" className="gg_plus_logo" />
        <p className="gg_plus_hub_headline">{UPSELL_HEADLINE}</p>
        <p className="gg_plus_hub_body">{UPSELL_BODY}</p>
        <Link to={MEMBERSHIP_PATH} className="green_text gg_plus_hub_link" onClick={handleHubLinkClick}>
          {linkText}
        </Link>
      </div>
    );
  }

  // Trial, active, past due, canceled, or lapsed — membership status summary
  return (
    <div className="gg_plus_hub_card">
      <img src={gg_plus_logo} alt="GG+" className="gg_plus_logo" />
      <p className="gg_plus_hub_card_title">GG+ Membership</p>

      <div className="gg_plus_hub_rows">
        <div className="gg_plus_hub_row">
          <span>Status</span>
          <span>{membershipStatusLabel(ent)}</span>
        </div>

        {level === 'past_due' ? (
          <div className="gg_plus_hub_row">
            <span>Details</span>
            <span>Update billing to restore GG+</span>
          </div>
        ) : null}

        {isGgPlus && priceLabel && !isMembershipCanceledOrEnding(ent) ? (
          <div className="gg_plus_hub_row">
            <span>Price</span>
            <span>{priceLabel}</span>
          </div>
        ) : null}

        {periodEnd && (isGgPlus || level === 'past_due') ? (
          <div className="gg_plus_hub_row">
            <span>{membershipPeriodEndLabel(ent)}</span>
            <span>{periodEnd}</span>
          </div>
        ) : null}
      </div>

      {cancelNotice ? (
        <p className="gg_plus_hub_notice">{cancelNotice}</p>
      ) : null}

      {cancelScheduled ? (
        <Gg_plus_reactivate_link label="Keep GG+" analyticsSource="account_hub" />
      ) : null}

      <Link to={MEMBERSHIP_PATH} className="green_text gg_plus_hub_link" onClick={handleHubLinkClick}>
        {linkText}
      </Link>
    </div>
  );
}

export default Gg_plus_membership_summary;
